/**
 * Left tree — domains, each expandable to its entities, plus an optional
 * Pinned section at the top (doc 147 §5.3 redesign · task #16).
 *
 * Entity clicks navigate straight to that entity's full-page detail screen
 * (`#entity/<id>`) via the shared `navigateToEntity` helper.
 *
 * Also includes 5 collapsible nav groups (exploration, intelligence, operations,
 * compliance, settings) for quick access to Atlas modules (doc 147 §5.2).
 *
 * Includes a filter input to narrow nav items and domains, with transient
 * auto-expand override for matching groups (not persisted to localStorage).
 */
import { useState, useMemo } from 'react';
import type { AtlasManifest, DomainLayer } from '@atlas-manifest/types';
import { Icon } from '../components/Icon.js';
import { domainIcon } from '../icons/catalog.js';
import { domainsSorted, entitiesInDomain, entityById, computeDomainStats } from '../state/selectors.js';
import { navigate, navigateToEntity, type Route } from '../state/hashRouter.js';
import { NAV_GROUPS, isGroupCollapsed, setGroupCollapsed, type NavGroupName } from '../state/navGroups.js';
import { DomainBadge } from './DomainBadge.js';
import { LayerBadge } from './LayerBadge.js';
import { ConnectivityPill } from './ConnectivityPill.js';

export interface DomainTreeProps {
  manifest: AtlasManifest;
  route: Route;
  /** Pinned entity ids; the Pinned section is hidden when empty. */
  pinned?: string[];
  /** Whether the sidebar is collapsed to icon-only mode (desktop rail). */
  collapsed?: boolean;
  /** Whether the mobile drawer is open (narrow viewports only). */
  mobileOpen?: boolean;
}

function activeEntityId(route: Route): string | undefined {
  if (route.kind === 'entity') return route.id;
  if (route.kind === 'workspace' && route.mode.kind !== 'idle') {
    return route.mode.entityId;
  }
  return undefined;
}

function activeDomainId(route: Route, manifest: AtlasManifest): string | undefined {
  if (route.kind === 'domain') return route.id;
  const entityId = activeEntityId(route);
  if (!entityId) return undefined;
  return entityById(manifest, entityId)?.domain;
}

export function DomainTree({
  manifest,
  route,
  pinned = [],
  collapsed = false,
  mobileOpen = false,
}: DomainTreeProps): React.JSX.Element {
  // `collapsed` is the *desktop* icon-rail preference, persisted independently
  // of viewport. When the mobile drawer is open, content must always show in
  // full regardless of that saved desktop preference — otherwise a user who
  // happens to have the desktop rail collapsed gets a drawer with no labels,
  // no filter, nothing but bare icons (see UX audit Phase 5 review).
  const effectiveCollapsed = collapsed && !mobileOpen;
  const activeEntity = activeEntityId(route);
  const initialExpanded = activeDomainId(route, manifest);
  const [expanded, setExpanded] = useState<string | null>(initialExpanded ?? null);
  const [filterText, setFilterText] = useState<string>('');
  const domains = domainsSorted(manifest);
  const statsMap = new Map(computeDomainStats(manifest).map((s) => [s.domainId, s]));

  const pinnedEntities = pinned
    .map((id) => entityById(manifest, id))
    .filter((e): e is NonNullable<typeof e> => Boolean(e));

  // Track collapse state for each nav group
  const [groupCollapseState, setGroupCollapseState] = useState<Record<NavGroupName, boolean>>(() => {
    const state: Record<NavGroupName, boolean> = {} as Record<NavGroupName, boolean>;
    NAV_GROUPS.forEach((group) => {
      state[group.name] = isGroupCollapsed(group.name);
    });
    return state;
  });

  const toggleGroupCollapsed = (groupName: NavGroupName): void => {
    const newState = !groupCollapseState[groupName];
    setGroupCollapseState((prev) => ({ ...prev, [groupName]: newState }));
    setGroupCollapsed(groupName, newState);
  };

  // Groups shown open only because they match the current filter aren't a
  // real user preference — clicking their header while filtered must not
  // silently rewrite the persisted collapse state (see UX audit review).
  const handleGroupHeaderClick = (groupName: NavGroupName, forcedOpenByFilter: boolean): void => {
    if (forcedOpenByFilter) return;
    toggleGroupCollapsed(groupName);
  };

  // Build a map of route kind to (label, icon, optional subtext) for quick lookup.
  // Only include routes that can be navigated to without a mandatory ID parameter.
  const moduleLabels: Record<string, { label: string; icon: string; subtext?: string }> = {
    'dashboard': { label: 'Dashboard', icon: 'dashboard' },
    'compliance-hq': { label: 'Compliance HQ', icon: 'verified_user' },
    'event-flow': { label: 'Event Flow', icon: 'timeline' },
    'pii-tracer': { label: 'PII Tracer', icon: 'policy' },
    'statemachine': { label: 'State Machines', icon: 'account_tree' },
    'simulate': { label: 'Simulation', icon: 'science' },
    'lineage': { label: 'Data Lineage', icon: 'account_tree' },
    'domain-map': { label: 'Domain Map', icon: 'view_in_ar' },
    'user-journeys': { label: 'User Journeys', icon: 'route' },
    'hub': { label: 'Hubs', icon: 'dashboard' },
    'ask': { label: 'AI Copilot', icon: 'smart_toy' },
    'live-monitor': { label: 'Live Monitor', icon: 'monitor_heart' },
    'constellation': { label: 'Workspace', icon: 'view_quilt' },
    'relationship-map': { label: 'Relationship Map', icon: 'hub' },
    'catalog': { label: 'Catalog', icon: 'library_books' },
    'catalog-graph': { label: 'Catalog Graph', icon: 'network_check' },
    'flag-catalog': { label: 'Flag Catalog', icon: 'toggle_on' },
    'patterns': { label: 'Read-Write Patterns', icon: 'schema' },
    'behavioral': { label: 'State Coverage Matrix', icon: 'account_tree' },
    'heatmap': { label: 'Heatmap', icon: 'heat_map' },
    'storyboard': { label: 'Journey Walkthrough', icon: 'dashboard' },
    'trace': { label: 'Trace', icon: 'account_tree' },
    'lint': { label: 'Linter', icon: 'check_circle' },
    'testdata': { label: 'Test Data', icon: 'data_usage' },
    'sandbox': { label: 'Sandbox', icon: 'sandbox' },
    'new-design-change': {
      label: 'New Design Change',
      icon: 'design_services',
      subtext: 'Guided kind-picker for a new sandbox proposal',
    },
    'diff': { label: 'Diff', icon: 'difference', subtext: 'Compare two manifest versions' },
    'drift': { label: 'Drift', icon: 'compare_arrows', subtext: 'Docs vs. model over time' },
    'workspace': { label: 'Workspace', icon: 'view_quilt' },
    'boards': { label: 'Boards', icon: 'dashboard' },
    'compliance': { label: 'Compliance', icon: 'verified_user' },
    'settings': { label: 'Settings', icon: 'settings' },
  };

  // Filter logic: case-insensitive substring match
  const filterLower = filterText.toLowerCase();
  const matchesFilter = (text: string): boolean => {
    return filterLower.length === 0 || text.toLowerCase().includes(filterLower);
  };

  // Compute which routes and domains match the filter
  const matchingRoutes = useMemo<Set<string>>(() => {
    if (filterLower.length === 0) return new Set();
    const matches = new Set<string>();
    NAV_GROUPS.forEach((group) => {
      group.routes.forEach((routeKind) => {
        const meta = moduleLabels[routeKind as keyof typeof moduleLabels];
        if (meta && matchesFilter(meta.label)) {
          matches.add(routeKind);
        }
      });
    });
    return matches;
  }, [filterLower]);

  const matchingDomains = useMemo<Set<string>>(() => {
    if (filterLower.length === 0) return new Set();
    const matches = new Set<string>();
    domains.forEach((domain) => {
      if (matchesFilter(domain.name)) {
        matches.add(domain.id);
      }
    });
    return matches;
  }, [filterLower, domains]);

  // Compute which groups should be auto-expanded for filtering
  const filterOverrideExpanded = useMemo<Set<NavGroupName>>(() => {
    if (filterLower.length === 0) return new Set();
    const expanded = new Set<NavGroupName>();
    NAV_GROUPS.forEach((group) => {
      const hasMatches = group.routes.some((routeKind) => matchingRoutes.has(routeKind));
      if (hasMatches) {
        expanded.add(group.name);
      }
    });
    return expanded;
  }, [filterLower, matchingRoutes]);

  const isFiltering = filterLower.length > 0;
  const hasNoMatches = isFiltering && matchingRoutes.size === 0 && matchingDomains.size === 0;
  const filterResultsAnnouncement = isFiltering
    ? hasNoMatches
      ? `No matches for "${filterText}"`
      : `${matchingRoutes.size} nav ${matchingRoutes.size === 1 ? 'item' : 'items'}, ${matchingDomains.size} ${matchingDomains.size === 1 ? 'domain' : 'domains'} match`
    : '';

  return (
    <nav
      className={`left-tree ${collapsed ? 'left-tree--collapsed' : ''} ${mobileOpen ? 'left-tree--mobile-open' : ''}`}
      aria-label="Domains"
    >
      {!effectiveCollapsed && (
        <div className="tree-brand">
          <span className="tree-brand-name">Atlas</span>
          <span className="tree-brand-sub">Brisarx data model</span>
        </div>
      )}

      {!effectiveCollapsed && (
        <input
          type="text"
          className="rail-filter-input"
          placeholder="Filter nav & domains…"
          value={filterText}
          onChange={(e) => setFilterText(e.currentTarget.value)}
          aria-label="Filter navigation and domains"
        />
      )}

      {!effectiveCollapsed && (
        <span className="visually-hidden" role="status" aria-live="polite">
          {filterResultsAnnouncement}
        </span>
      )}

      {!effectiveCollapsed && hasNoMatches && (
        <div className="tree-filter-empty">No matches for “{filterText}”.</div>
      )}

      {pinnedEntities.length > 0 && !effectiveCollapsed && (
        <>
          <div className="tree-section-label">Pinned · {pinnedEntities.length}</div>
          {pinnedEntities.map((entity) => (
            <button
              key={`pin-${entity.id}`}
              className="tree-row tree-pinned"
              aria-current={activeEntity === entity.id}
              onClick={() => navigateToEntity(entity.id)}
            >
              <span className="mono">★ {entity.name}</span>
            </button>
          ))}
        </>
      )}

      {/* Collapsible nav groups */}
      {!effectiveCollapsed &&
        NAV_GROUPS.map((group) => {
          const isCollapsed = groupCollapseState[group.name];
          const shouldAutoExpandForFilter = filterOverrideExpanded.has(group.name);
          const effectivelyExpanded = shouldAutoExpandForFilter || !isCollapsed;
          const visibleRoutes = group.routes.filter((routeKind) => {
            if (filterLower.length === 0) return true;
            return matchingRoutes.has(routeKind);
          });

          // Hide group entirely if filter is active and no routes match
          if (filterLower.length > 0 && visibleRoutes.length === 0) {
            return null;
          }

          return (
            <div key={`nav-group-${group.name}`} className="nav-group">
              <button
                className="nav-group-header"
                aria-expanded={effectivelyExpanded}
                aria-disabled={shouldAutoExpandForFilter}
                title={shouldAutoExpandForFilter ? 'Open because it matches your filter' : undefined}
                onClick={() => handleGroupHeaderClick(group.name, shouldAutoExpandForFilter)}
              >
                <Icon name={group.icon} size="sm" />
                <span className="nav-group-label">{group.label}</span>
                <Icon
                  name={effectivelyExpanded ? 'expand_more' : 'chevron_right'}
                  size="sm"
                  className="nav-group-chevron"
                />
              </button>
              {effectivelyExpanded &&
                visibleRoutes.map((routeKind) => {
                  const meta = moduleLabels[routeKind];
                  if (!meta) return null;
                  return (
                    <div key={routeKind}>
                      <button
                        className="tree-row"
                        aria-current={route.kind === routeKind}
                        onClick={() =>
                          routeKind === 'workspace'
                            ? navigate({ kind: 'workspace', mode: { kind: 'idle' } })
                            : navigate({ kind: routeKind } as Route)
                        }
                        title={effectiveCollapsed ? meta.label : undefined}
                      >
                        <Icon name={meta.icon} size="sm" />
                        <span>{meta.label}</span>
                      </button>
                      {meta.subtext && !effectiveCollapsed && (
                        <div className="tree-row-subtext">{meta.subtext}</div>
                      )}
                    </div>
                  );
                })}
            </div>
          );
        })}

      {!effectiveCollapsed && filterLower.length === 0 && (
        <div className="tree-section-label">Domains · {domains.length}</div>
      )}
      {!effectiveCollapsed && filterLower.length > 0 && matchingDomains.size > 0 && (
        <div className="tree-section-label">Domains · {matchingDomains.size}</div>
      )}
      {domains
        .filter((d) => filterLower.length === 0 || matchingDomains.has(d.id))
        .map((domain) => {
          const isExpanded = expanded === domain.id;
          const stats = statsMap.get(domain.id);
          return (
            <div key={domain.id}>
              <button
                className="tree-row"
                aria-current={route.kind === 'domain' && route.id === domain.id}
                aria-expanded={isExpanded}
                onClick={() => {
                  setExpanded(isExpanded ? null : domain.id);
                  navigate({ kind: 'domain', id: domain.id });
                }}
                title={effectiveCollapsed ? domain.name : undefined}
              >
                <Icon name={domainIcon(domain.id)} size="sm" color={`var(--${domain.color.replace(/^--/, '')})`} />
                {!effectiveCollapsed && (
                  <>
                    <DomainBadge domainId={domain.id} size="sm" />
                    <LayerBadge layer={(domain.layer as DomainLayer) ?? 'L5'} size="sm" />
                    {stats && <ConnectivityPill status={stats.connectivity} />}
                    <span className="tree-row-count">{domain.entityCount}</span>
                  </>
                )}
              </button>
              {isExpanded &&
                entitiesInDomain(manifest, domain.id).map((entity) => (
                  <button
                    key={entity.id}
                    className="tree-row tree-entity"
                    aria-current={activeEntity === entity.id}
                    onClick={() => navigateToEntity(entity.id)}
                  >
                    <span className="mono">{entity.name}</span>
                  </button>
                ))}
            </div>
          );
        })}
    </nav>
  );
}
